/**
 * /fr/recherche-produit — product discovery concept page (French). Targets the
 * REAL keyword "recherche produit" (90). Links /fr/retranscription-entretien +
 * /fr/prd-document-exigences-produit.
 */
import { MarketingShell } from './MarketingShell';

export function FrRechercheProduit() {
  return (
    <MarketingShell
      currentLocale="fr"
      hreflangPath="/recherche-produit"
      hreflangLocales={['fr']}
      documentTitle="Recherche produit & product discovery : méthode + outils · Draftlize"
      documentDescription="La recherche produit (discovery) en pratique : méthodes d'entretien, synthèse des insights, et comment transformer les entretiens en décisions produit traçables."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">Guide · Discovery</span>
          <h1 className="serif" style={{ fontSize: 'clamp(33px, 4.4vw, 56px)', lineHeight: 1.05, margin: '12px 0 24px' }}>
            Recherche produit (product discovery) :<br />
            <em>du terrain à la décision.</em>
          </h1>
          <p className="lede">
            La recherche produit (product discovery) consiste à valider <em>quoi</em> construire avant de le construire : entretiens, observation, tests, pour transformer les hypothèses en faits. Même bien menée, le maillon faible est toujours au même endroit : les <em>insights se perdent avant de devenir des décisions</em>. Voici les méthodes, et comment transformer les entretiens en décisions traçables.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">Les méthodes de recherche produit.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ul style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 20 }}>
            <li><strong>Entretiens utilisateurs</strong> — écoutez le problème et le contexte, pas des réponses.</li>
            <li><strong>Observation et données d'usage</strong> — regardez ce qu'ils font, pas seulement ce qu'ils disent.</li>
            <li><strong>Validation (prototypes, tests)</strong> — éprouvez les hypothèses à petite échelle.</li>
            <li><strong>Synthèse</strong> — condensez les trouvailles éparses en motifs suffisants pour décider.</li>
          </ul>
          <p style={{ fontSize: 16, lineHeight: 1.8, color: 'var(--ink-1)', margin: '20px 0 0' }}>
            Commencez par <a href="/fr/retranscription-entretien" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>retranscrire l'entretien</a> pour avoir la matière première. Mais le verbatim n'est pas la fin : c'est le point de départ vers la synthèse et la décision.
          </p>
        </div>
      </section>

      <section className="problem" style={{ background: 'var(--paper-1)' }}>
        <div className="head">
          <h2 className="serif">Le maillon faible : entre trouvaille et décision. <em>Reliez-le.</em></h2>
          <p style={{ maxWidth: 720, marginTop: 12, color: 'var(--ink-1)', fontSize: 16 }}>
            Le résultat de la recherche produit, ce ne sont pas les phrases des entretiens, mais les <em>décisions</em> que vous en tirez. Beaucoup d'équipes résument les trouvailles dans une présentation et s'arrêtent là : la décision et son pourquoi ne vivent nulle part. Draftlize relie « verbatim → décision → exigence ».
          </p>
        </div>
        <div className="problem-grid">
          <div className="problem-col">
            <span className="num">I</span>
            <h3 className="serif">Pourquoi les insights se perdent</h3>
            <p>Les moments forts de l'entretien s'éparpillent dans des pages Notion ou des comptes rendus, et au sprint suivant personne ne les rouvre, car le « pourquoi » de la décision est coupé de la trouvaille.</p>
          </div>
          <div className="problem-col">
            <span className="num">II</span>
            <h3 className="serif">Reliez « verbatim → décision → exigence »</h3>
            <p>Dans Draftlize, vous reliez la phrase d'un utilisateur à la décision qu'elle a déclenchée et à l'exigence qui en découle. Si un test ultérieur renverse l'hypothèse, ce qui en dépend passe en « stale ».</p>
          </div>
          <div className="problem-col">
            <span className="num">III</span>
            <h3 className="serif">Votre agent IA part des trouvailles</h3>
            <p>Les décisions de discovery deviennent un contexte que Claude Code ou Cursor lisent via MCP. La prochaine spec s'écrit en sachant « l'utilisateur a dit ceci, donc nous avons décidé cela ». Poursuivez avec le <a href="/fr/prd-document-exigences-produit" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>PRD</a>.</p>
          </div>
        </div>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          La valeur de la recherche produit ne tient pas au nombre d'insights collectés, mais au fait que, six mois plus tard, vous pouvez encore retrouver les décisions qu'ils ont fondées.
        </blockquote>
        <cite>Reliez la trouvaille à la décision, et signalez quand l'hypothèse change. C'est Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">FAQ</span>
            <h2 className="serif">Questions fréquentes.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'Discovery ou delivery ?', a: 'Le discovery valide « construire la bonne chose » ; le delivery, c\'est « bien la construire ». Ils vont ensemble, mais si les décisions du discovery ne restent pas, le delivery vise à côté.' },
            { q: 'Combien d\'entretiens ?', a: 'Plus qu\'un nombre exact, jusqu\'à ne plus obtenir de trouvailles nouvelles (saturation). L\'important n\'est pas le nombre, mais à quelle décision vous avez relié chaque phrase.' },
            { q: 'Comment synthétiser les trouvailles ?', a: 'Regroupez les phrases par thème et cherchez des motifs suffisants pour décider. Dans Draftlize, chaque motif devient une carte de décision reliée aux phrases d\'origine.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.8, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">Reliez chaque phrase d'entretien à la décision <em>— gratuit avec 5 $.</em></h2>
        </div>
        <div className="trust-strip">
          <span>5 $ offerts à l'inscription</span>
          <span>Payez seulement ce que vous utilisez</span>
          <span>Le solde n'expire jamais</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            Transformez les trouvailles de la recherche produit en cartes « verbatim → décision → exigence » dans Draftlize. Si l'hypothèse change, elle passe en « stale » toute seule, et votre agent IA la lit.
          </p>
          <a className="btn-primary" href="/">Commencer gratuitement avec 5 $</a>
        </div>
      </section>
    </MarketingShell>
  );
}
